import ActionButton from "@/components/Button/ActionButton";
import { Spacer, VStack } from "@/components/Layout";
import { FullScreenOverlay } from "@/components/Loading";
import { Text } from "@/components/Typography/Text";
import { Separator } from "@/components/ui/separator";
import { toast } from "@/components/ui/use-toast";
import { useCommonTranslation } from "@/hooks/useCommonTranslation";
import { apiClient } from "@/lib/api/client";
import { getDataOrThrowApiError, handleApiError } from "@/lib/api/error";
import { defaultNS } from "@/lib/i18n";
import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAllowIndividualRequestOperationPackageQuery } from "../../-query/useAllowIndividualRequestOperationPackageQuery";
import { ApplicationOperationPackageEligibleCards } from "./-components/ApplicationOperationPackageEligibleCards";
import { ApplicationOperationPackagePatternCards } from "./-components/ApplicationOperationPackagePatternCards";
import { useStartOperationPackageByEligibleTarget } from "./-query";

export const Route = createFileRoute(
  "/_individual/individual/operation-package/$id/",
)({
  component: RouteComponent,
});

function RouteComponent() {
  const { id } = Route.useParams();
  const { data } = useAllowIndividualRequestOperationPackageQuery(id);
  const hasEligibleTarget = data.packageType === "AppUserCreation";

  return (
    <>
      {hasEligibleTarget ? (
        <EligibleTargetApplication id={id} name={data.name} />
      ) : (
        <DefaultApplication id={id} name={data.name} />
      )}
    </>
  );
}

/**
 * 対象を選択してもらうタイプの手続き
 */
const EligibleTargetApplication = ({
  id,
  name,
}: {
  id: string;
  name: string;
}) => {
  const navigate = Route.useNavigate();
  const { i18n } = useTranslation();

  const { t } = useTranslation(defaultNS, {
    keyPrefix: "individualPages.operationPackage",
  });
  const [targetId, setTargetId] = useState<string>("");
  const { mutate, isPending } = useStartOperationPackageByEligibleTarget(
    id,
    targetId,
    {
      onSuccess: async (data) => {
        try {
          // タスク詳細APIを叩いて次のタスクIDを取得
          const response = await apiClient.GET("/v2/self-service/tasks/{id}", {
            params: {
              path: {
                id: data.operationPackageInstanceId,
              },
            },
          });
          const taskDetail = getDataOrThrowApiError(response);

          // NotStartedまたはInProgressのタスクを探す
          let nextTaskId: string | null = null;
          for (const step of taskDetail.steps) {
            for (const task of step.tasks) {
              if (
                task.status === "NotStarted" ||
                task.status === "InProgress"
              ) {
                nextTaskId = task.taskInstanceId;
                break;
              }
            }
            if (nextTaskId) break;
          }

          // 次のタスクが見つかった場合はフォームページへ遷移
          if (nextTaskId) {
            navigate({
              to: "/individual/task/$id/form/$formId",
              params: {
                id: data.operationPackageInstanceId,
                formId: nextTaskId,
              },
            });
          } else {
            // 次のタスクが見つからない場合はタスクページへ遷移
            navigate({
              to: `/individual/task/${data.operationPackageInstanceId}`,
            });
          }
        } catch (error) {
          console.error("Failed to fetch task detail:", error);
          // エラー時はタスクページへ遷移
          navigate({
            to: `/individual/task/${data.operationPackageInstanceId}`,
          });
        }
      },
      onError: (error) => {
        const { message } = handleApiError(error, {
          language: i18n.language,
        });
        toast({
          description: message,
          variant: "destructive",
        });
      },
    },
  );

  const handleButtonClick = (targetId: string) => {
    setTargetId(targetId);
  };

  const startPackage = async () => {
    await mutate({});
  };

  return (
    <VStack className="py-3 px-5 gap-3 items-start max-w-[640px] mx-auto">
      <ActionButton
        icon="ChevronLeft"
        iconColor="muted"
        variant="ghost"
        className="p-0 text-neutral-muted-on-fill font-semibold h-fit"
        onClick={() => {
          navigate({ to: "/individual" });
        }}
      >
        {t("navigateToHome")}
      </ActionButton>
      <VStack className="gap-2">
        <Text variant="h2">{name}</Text>
        <Text variant="labelLarge">{t("page.selectPattern.description")}</Text>
      </VStack>
      <div className="py-3 w-full">
        <Separator />
      </div>
      <Spacer variant="s" />
      <ApplicationOperationPackageEligibleCards
        packageId={id}
        onClick={handleButtonClick}
      />
      <Spacer variant="s" />
      <ActionButton
        onClick={startPackage}
        className="w-full"
        disabled={!targetId}
      >
        {t("page.startOperationPackage.startOperationPackage", {
          operationPackageName: name,
        })}
      </ActionButton>
      {isPending && <FullScreenOverlay />}
    </VStack>
  );
};

/**
 * Patternを明示的に選択してもらうタイプの手続き
 */
const DefaultApplication = ({ id, name }: { id: string; name: string }) => {
  const navigate = Route.useNavigate();

  const { t } = useTranslation(defaultNS, {
    keyPrefix: "individualPages.operationPackage",
  });
  const tc = useCommonTranslation();

  const [selectedPackagePatternId, setSelectedPackagePatternId] =
    useState<string>("");

  const handleButtonClick = useCallback(() => {
    if (selectedPackagePatternId) {
      navigate({
        to: `/individual/operation-package/${id}/pattern/${selectedPackagePatternId}`,
      });
    }
  }, [id, selectedPackagePatternId, navigate]);

  return (
    <VStack className="py-3 px-5 gap-3 items-start max-w-[640px] mx-auto">
      <ActionButton
        icon="ChevronLeft"
        iconColor="muted"
        variant="ghost"
        className="p-0 text-neutral-muted-on-fill font-semibold h-fit"
        onClick={() => {
          navigate({ to: "/individual" });
        }}
      >
        {t("navigateToHome")}
      </ActionButton>
      <VStack className="gap-2">
        <Text variant="h2">{name}</Text>
        <Text variant="labelLarge">{t("page.selectPattern.description")}</Text>
      </VStack>
      <div className="py-3 w-full">
        <Separator />
      </div>
      <Spacer variant="s" />
      <ApplicationOperationPackagePatternCards
        operationPackageId={id}
        selectedPackagePatternId={selectedPackagePatternId}
        setSelectedPackagePatternId={setSelectedPackagePatternId}
      />
      <Spacer variant="s" />
      <ActionButton
        onClick={handleButtonClick}
        className="w-full"
        disabled={!selectedPackagePatternId}
      >
        {tc("action.next")}
      </ActionButton>
    </VStack>
  );
};
